import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, Globe, Users, MessageSquare, Upload, ArrowRight, CheckCircle, Sparkles, TrendingUp, Brain, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';

export default function EnhancedLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/20 to-black"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <Navigation />

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative min-h-screen flex items-center">
          <div className="max-w-7xl mx-auto px-6 py-32">
            <motion.div 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 1, ease: "easeOut" }}
              className="text-center"
            >
              {/* Badge */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20"
              >
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-400 font-medium">268+ AI Tools • Real-time Auctions • Creator Hub</span>
              </motion.div>

              {/* Main Heading */}
              <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 leading-tight">
                <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  AI-Powered
                </span>
                <span className="block text-white mt-2">
                  Marketplace
                </span>
              </h1>

              {/* Subheading */}
              <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed">
                Build, trade, and scale with the most advanced AI platform. 
                <span className="text-teal-400"> Real-time auctions</span>, 
                <span className="text-purple-400"> seamless payments</span>, and 
                <span className="text-cyan-400"> limitless AI tools</span>.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-2xl shadow-purple-500/50 transition-all duration-300 hover:scale-105 border-0"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => navigate('/ai-tools')}
                  className="border-2 border-purple-500/50 text-white hover:bg-purple-500/10 px-8 py-6 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-105"
                >
                  Explore AI Tools
                </Button>
              </div>

              {/* Stats */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 max-w-4xl mx-auto"
              >
                {[
                  { value: '268+', label: 'AI Tools' },
                  { value: '50K+', label: 'Active Users' },
                  { value: '99.9%', label: 'Uptime' },
                  { value: '24/7', label: 'Support' }
                ].map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-4xl md:text-5xl font-black bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-32 relative">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-20"
            >
              <h2 className="text-5xl md:text-6xl font-black mb-6">
                Everything You Need
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Built for creators, entrepreneurs, and AI enthusiasts
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { 
                  icon: Brain, 
                  title: '268+ AI Tools', 
                  desc: 'From image generation to code completion, access the latest AI models in one place',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                { 
                  icon: MessageSquare, 
                  title: 'Real-time Chat', 
                  desc: 'Connect with vendors, creators, and buyers instantly with Firebase-powered messaging',
                  gradient: 'from-purple-500 to-pink-500'
                },
                { 
                  icon: TrendingUp, 
                  title: 'Live Auctions', 
                  desc: 'Bid on exclusive items with WebSocket real-time updates and instant notifications',
                  gradient: 'from-teal-500 to-cyan-500'
                },
                { 
                  icon: Upload, 
                  title: 'Cloud Storage', 
                  desc: 'Upload, manage, and share files with Cloudflare R2 and Firebase Storage integration',
                  gradient: 'from-blue-500 to-purple-500'
                },
                { 
                  icon: Shield, 
                  title: 'Secure Payments', 
                  desc: 'Stripe Live Mode with buyer protection, subscriptions, and instant payouts',
                  gradient: 'from-cyan-500 to-teal-500'
                },
                { 
                  icon: Rocket, 
                  title: 'AI Agents', 
                  desc: 'Autonomous agents powered by Gemini 2.5 Flash for content moderation and support',
                  gradient: 'from-purple-500 to-blue-500'
                }
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.6 }}
                  className="group relative"
                >
                  <div className="h-full p-8 rounded-3xl bg-gradient-to-br from-zinc-900/50 to-black border border-zinc-800 hover:border-purple-500/50 transition-all duration-300 backdrop-blur-sm">
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} p-4 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon className="w-full h-full text-white" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-cyan-400 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Social Proof */}
        <div className="py-32 relative">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/20">
                <CheckCircle className="w-4 h-4 text-teal-400" />
                <span className="text-sm text-teal-400 font-medium">Trusted by 50,000+ users worldwide</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-black mb-12">
                Why Choose <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">NEXUS</span>?
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                {[
                  { title: 'Lightning Fast', desc: 'Built on Cloudflare Pages with global CDN' },
                  { title: 'Production Ready', desc: 'Firebase, Stripe, and enterprise-grade security' },
                  { title: 'AI-First', desc: 'Powered by Gemini 2.5 Flash and Claude Sonnet 4' }
                ].map((item, idx) => (
                  <div key={idx} className="p-6 rounded-2xl bg-zinc-900/30 border border-zinc-800">
                    <h3 className="text-xl font-bold mb-3 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                      {item.title}
                    </h3>
                    <p className="text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-32 relative">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-600 p-12 md:p-20 text-center"
            >
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-6xl font-black mb-6">
                  Ready to Get Started?
                </h2>
                <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  Join thousands of creators and entrepreneurs building the future with AI
                </p>
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  className="bg-black hover:bg-zinc-900 text-white px-10 py-6 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-105 border-0"
                >
                  Start Your Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="mt-6 text-white/70 text-sm">No credit card required • 14-day free trial</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-12 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-6 h-6 text-cyan-400" />
                  <span className="text-xl font-black bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    NEXUS
                  </span>
                </div>
                <p className="text-gray-500 text-sm">
                  The world's most advanced AI-powered marketplace
                </p>
              </div>
              
              <div>
                <h4 className="font-bold mb-4">Product</h4>
                <div className="space-y-2">
                  <a href="/features" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">Features</a>
                  <a href="/pricing" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">Pricing</a>
                  <a href="/ai-tools" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">AI Tools</a>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-4">Company</h4>
                <div className="space-y-2">
                  <a href="/about" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">About</a>
                  <a href="/contact" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">Contact</a>
                  <a href="/docs" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">Documentation</a>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-4">Legal</h4>
                <div className="space-y-2">
                  <a href="/privacy" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">Privacy</a>
                  <a href="/terms" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">Terms</a>
                  <a href="/contact" className="block text-gray-500 hover:text-cyan-400 transition-colors text-sm">Support</a>
                </div>
              </div>
            </div>

            <div className="text-center pt-8 border-t border-zinc-800">
              <p className="text-gray-500 text-sm">
                © 2026 NEXUS AI Social Marketplace. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
